import {
  localStorageKeysConstants,
  TRZ_TERMS,
  websocketActionsConstants,
} from '../../constants';
import {firebaseAnalytics} from '../../snippets/firebase';
import {helpers as servicesHelpers} from '../../state/services/helpers';
import {objectHelpers} from './object.helpers';
import routes from '../../routes/routes.json';
import routesList from '../../routes/routes.json';

const {COMPANY_ID, TOKEN} = localStorageKeysConstants;

/**
 * Collection of system-level utility helpers for feature flags, URLs, auth, and analytics.
 * @namespace systemHelpers
 * @example
 * import { systemHelpers } from './system.helpers';
 */
export const systemHelpers = {
  /**
   * Checks whether a given feature is enabled via the REACT_APP_AVAILABLE_FEATURES env variable.
   * @param {string} feature - Feature name to look up (e.g. 'budgets', 'cards').
   * @returns {boolean} True if the feature is in the available features list.
   * @example
   * systemHelpers.checkIfAvailableFeature('budgets'); // true
   */
  checkIfAvailableFeature: (feature) => {
    const availableFeatures = process.env.REACT_APP_AVAILABLE_FEATURES;
    if (availableFeatures) {
      const features = process.env.REACT_APP_AVAILABLE_FEATURES.split(',');
      return features.includes(feature);
    } else {
      return false;
    }
  },
  /**
   * Builds a deep-link URL for the mobile app, using the configured release channel and base link.
   * @param {string} path - Path within the mobile app (without leading slash).
   * @returns {string} Full mobile app URL including release channel query param when configured.
   * @example
   * systemHelpers.getMobileAppPath('expenses/123'); // 'https://app.example.com/--/expenses/123'
   */
  getMobileAppPath: (path) => {
    const releaseChannel = process.env.REACT_APP_MOBILE_APP_RELEASE_CHANNEL;
    const additionalPath = releaseChannel ? `?release-channel=${releaseChannel}` : '';
    const link = process.env.REACT_APP_MOBILE_APP_LINK;
    const linkPath = `/${path}${additionalPath}`;
    return link ? `${link}/--${linkPath}` : `h1card:/${linkPath}`;
  },
  /**
   * Returns the WebSocket URL for the given variant, upgrading ws:// to wss://.
   * @param {'agentChat'|'authentication'|'backend'} variant - Which WebSocket endpoint to retrieve.
   * @returns {string|undefined} Secure WebSocket URL, or undefined if not configured.
   * @example
   * systemHelpers.getWebsocketUrl('backend'); // 'wss://api.example.com/ws'
   */
  getWebsocketUrl: (variant) => {
    const socketUrls = {
      accounting: process.env.REACT_APP_ACCOUNTING_WEBSOCKET_ENDPOINT,
      agentChat: process.env.REACT_APP_AGENT_CHAT_WEBSOCKET,
      authentication: process.env.REACT_APP_AUTHENTICATION_WEBSOCKET_ENDPOINT,
      backend: process.env.REACT_APP_WEBSOCKET_ENDPOINT,
      openBanking: process.env.REACT_APP_OPEN_BANKING_WEBSOCKET_ENDPOINT,
    };
    const url = (socketUrls[variant] || '').replace('ws://', 'wss://');
    return url || undefined;
  },
  /**
   * Reads the company ID, access token, token expiry, and expired status from localStorage.
   * @returns {{companyId: string|null, token: string|null, expiresAt: number|null, isExpired: boolean}} Object with companyId, token, expiresAt (Unix seconds), and isExpired values.
   * @example
   * const { companyId, token, isExpired } = systemHelpers.getAuthDetails();
   */
  getAuthDetails: () => {
    const storedToken = JSON.parse(localStorage.getItem(TOKEN));
    const expiresAt = objectHelpers.getObjProp(storedToken, 'expiresAt');
    const token = objectHelpers.getObjProp(storedToken, 'accessToken');
    return {
      companyId: localStorage.getItem(COMPANY_ID),
      token,
      expiresAt,
      // treat token as expired 60s early to account for network/request latency
      isExpired: expiresAt ? Date.now() / 1000 > expiresAt - 60 : !token
    };
  },
  /**
   * Returns the default react-use-websocket options with exponential back-off reconnect logic.
   * @returns {{shouldReconnect: Function, reconnectAttempts: number, reconnectInterval: Function}} WebSocket options object.
   * @example
   * const options = systemHelpers.getWebsocketOptions();
   */
  getWebsocketOptions: ({useHeartbeat = true} = {}) => {
    const options = {
      shouldReconnect: () => true,
      reconnectAttempts: Number.MAX_SAFE_INTEGER,
      reconnectInterval: (attemptNumber) =>
        Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
    };
    if (useHeartbeat) {
      options.heartbeat = {
        message: JSON.stringify({action: websocketActionsConstants.PING}),
        interval: 60000,
        timeout: 120000,
      };
    }
    return options;
  },
  /**
   * Returns true when the JWT token's TTL has not yet expired.
   * @param {string|Object} token - Raw JWT string or pre-parsed payload object.
   * @param {boolean} [needParse=true] - When true the token is parsed from a JWT string; false skips parsing.
   * @returns {boolean} True if the current time is before the token's expiry.
   * @example
   * systemHelpers.checkAvailableTokenTTL(rawJwtString); // true
   */
  checkAvailableTokenTTL: (token, needParse = true) => {
    const data = needParse ? servicesHelpers.parseJWT(token) : token;
    const ttl = objectHelpers.getObjProp(data, 'token_ttl', 0);
    return ttl ? new Date().getTime() < ttl * 1000 : false;
  },
  /**
   * Returns the Treezor terms-and-conditions URL for the given locale, falling back to the default.
   * @param {string|null} locale - BCP 47 locale code (e.g. 'fr', 'en'). Case-insensitive.
   * @returns {string} URL string for the applicable Treezor terms page.
   * @example
   * systemHelpers.getTrzTermsUrl('fr'); // 'https://treezor.com/fr/terms'
   */
  getTrzTermsUrl: (locale) => {
    const defaultLink = TRZ_TERMS.default;
    let termsLink;
    if (locale && typeof locale === 'string') {
      locale = locale.toUpperCase();
      termsLink = TRZ_TERMS.hasOwnProperty(locale) ? TRZ_TERMS[locale] : TRZ_TERMS.default
    } else {
      termsLink = defaultLink;
    }
    return termsLink;
  },
  /**
   * Logs a Firebase Analytics event with the given name and optional parameters.
   * @param {string} eventName - Name of the analytics event.
   * @param {Object} [eventParams] - Optional key/value parameters attached to the event.
   * @returns {void}
   * @example
   * systemHelpers.logEvent('page_view', { page_title: 'Dashboard' });
   */
  logEvent: (eventName, eventParams) => firebaseAnalytics.logEvent(eventName, eventParams),
  /**
   * Builds the breadcrumb array for a user based on their role and overview availability.
   * @param {Object} params - Parameters object.
   * @param {Array<{title: string, href?: string}>} params.defaultBreadcrumbs - Full breadcrumb trail as defined by the page.
   * @param {boolean} params.hasAdminAccess - When true, returns the full defaultBreadcrumbs unchanged.
   * @param {boolean} params.isEnabledUserOverview - When true, prepends the Home breadcrumb for non-admin users.
   * @param {Function} params.t - i18n translation function.
   * @returns {Array<{title: string, href?: string}>} Adjusted breadcrumb array for the current user.
   * @example
   * systemHelpers.getUserBreadcrumbs({ defaultBreadcrumbs, hasAdminAccess: false, isEnabledUserOverview: true, t });
   */
  getUserBreadcrumbs: ({defaultBreadcrumbs, hasAdminAccess, isEnabledUserOverview, t}) => {
    let breadcrumbs = [];
    if (hasAdminAccess) {
      breadcrumbs = defaultBreadcrumbs;
    } else if (defaultBreadcrumbs.length > 1) {
      breadcrumbs = [...defaultBreadcrumbs.slice(1)];
      if (isEnabledUserOverview) {
        breadcrumbs = [
          {title: t('main:home'), href: routes.overview},
          ...breadcrumbs
        ];
      }
    }
    return breadcrumbs;
  },
  /**
   * Returns true when both COMPANY_ID and TOKEN exist in localStorage, indicating an active session.
   * @returns {boolean} True if the user has an active authenticated session.
   * @example
   * if (systemHelpers.hasActiveSession()) { // proceed to dashboard }
   */
  hasActiveSession: () => Boolean(localStorage.getItem(COMPANY_ID) && localStorage.getItem(TOKEN)),
  /**
   * Returns true when the app is running in the production Node environment.
   * @returns {boolean} True if NODE_ENV is 'production'.
   * @example
   * if (systemHelpers.isProduction()) { enableSentry(); }
   */
  isProduction: () => process.env.NODE_ENV === 'production',
  /**
   * Returns the list of route paths that are publicly accessible without authentication.
   * @returns {string[]} Array of public route path strings.
   * @example
   * const publicPaths = systemHelpers.getPublicPaths();
   */
  getPublicPaths: () => {
    return [
      routesList.verifyIdentity, routesList.userInvite, routesList.oauthCallback, routesList.publicSubscriptionUpdate,
      routesList.quickGoogleConnect, routesList.connectReturn
    ]
  },
  /**
   * Extracts the target company id from a cross-company access error (HTTP 412).
   * The backend returns 412 with a `company_id` in the body when a resource belongs
   * to another company the user may switch to.
   * @param {Object} error - The axios error passed to a request error callback.
   * @returns {string|null} The company id to switch to, or null when not a cross-company 412.
   * @example
   * const companyId = systemHelpers.getCrossCompanyRedirectId(error);
   */
  getCrossCompanyRedirectId: (error) => {
    if (error?.response?.status !== 412) return null;
    return error?.response?.data?.company_id || null;
  },
}
